import { useState } from "react";

import {
  Cancel,
  Info,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Warning,
} from "@mui/icons-material";
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import _ from "lodash";
import { BeaconIcon } from "src/components/StyledComponents";

import { ItemPreviewImageButton } from "@features/items";
import { PaperTooltip, Type } from "@features/ui";
import { Brand } from "@models/Brand";
import { OrderSetVariant } from "@models/OrderSetVariant";
import { formatLocalDate } from "@utils/date";

import CompareCosts from "../CompareCosts";
import { getCoopCost, getCost, getVariantName } from "../helpers";
import EditOrderVariantDeliveryDateModal from "./EditOrderVariantDeliveryDateModal";
import "./OrderSetTable.css";
import OrderSetVariantRequestedDate from "./OrderSetVariantRequestedDate";
import StateCodeFilter from "./StateCodeFilter";
import OrderSetAddressSection from "./addressSelection/OrderSetAddressSection";
import { useCurrentOrderSet } from "./data/CurrentOrderSetContext";
import useDeleteOrderSetVariant from "./data/mutations/useDeleteOrderSetVariant";

const Brands = ({ brands }: { brands: Brand[] }) => {
  if (brands.length > 3) {
    return (
      <div className="flex justify-center items-center gap-2">
        Multi-brand
        <PaperTooltip
          title={
            <ul className="list-disc list-inside">
              {brands.map((v) => (
                <li key={v.id}>{v.name}</li>
              ))}
            </ul>
          }
        >
          <div className="text-xs py-1 px-2 ml-1 rounded bg-neutral-200 text-neutral-600 cursor-pointer hover:bg-neutral-300">
            {brands.length}
          </div>
        </PaperTooltip>
      </div>
    );
  }
  return <span>{brands.map((b) => b.name).join(", ")}</span>;
};

const OrderSetHead = ({
  measuredRef,
  stateCodeFilter,
  setStateCodeFilter,
}: {
  measuredRef: (node: HTMLTableSectionElement) => void;
  stateCodeFilter: string | null;
  setStateCodeFilter: (stateCode: string | null) => void;
}) => {
  const { orderSet, orderSetVariants, isCoopOrderSet } = useCurrentOrderSet();
  const deleteOrderSetVariant = useDeleteOrderSetVariant();
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(true);

  const [editingOrderSetVariantDelivery, setEditingOrderSetVariantDelivery] =
    useState<null | OrderSetVariant>(null);

  return (
    <>
      {editingOrderSetVariantDelivery && (
        <EditOrderVariantDeliveryDateModal
          orderSetVariant={editingOrderSetVariantDelivery}
          onClose={() => setEditingOrderSetVariantDelivery(null)}
        />
      )}
      <TableHead ref={measuredRef} className="sticky top-0 z-30">
        <TableRow>
          <TableCell className="sticky left-0 p-6 align-top">
            {["inactive", "in-progress", "submitted"].includes(
              orderSet.status
            ) && !orderSet.markedForApproval ? (
              <OrderSetAddressSection />
            ) : (
              <Type.P className="text-lg font-normal">
                {orderSet.approvedAt
                  ? `Order set was approved on ${formatLocalDate(orderSet.approvedAt)}`
                  : orderSet.markedForApprovalAt
                    ? `Order set was marked for approval on ${formatLocalDate(orderSet.markedForApprovalAt)}`
                    : `Order Set is ${_.startCase(orderSet.status)}`}
                . No new orders can be added at this time.
              </Type.P>
            )}
          </TableCell>
          {orderSetVariants.map((osv, i) => (
            <TableCell
              key={`head-cell-${i}`}
              className="align-top sticky left-0"
            >
              <div className="absolute z-10 top-0 left-0 flex gap-2">
                <OrderSetVariantRequestedDate
                  orderSetVariant={osv}
                  orderSetType={orderSet.type}
                />
              </div>
              <div className="absolute z-10 top-0 right-0 flex gap-2">
                {osv.variant.item.isCustomCoupon && (
                  <Tooltip title="If you are ordering Coupon Tear Sheets or Post It Tear Pad Coupons, please remember that there are 10 coupons per sheet or pad. Please order the number of Sheets or Pads you want.">
                    <Info fontSize="small" />
                  </Tooltip>
                )}
                {osv.isRush && orderSet.status === "submitted" && (
                  <Tooltip
                    title={`Standard Delivery: ${formatLocalDate(
                      osv.estimatedInMarketDate
                    )}, Requested Delivery: ${formatLocalDate(
                      osv.inMarketDate!
                    )}, Click to change.`}
                  >
                    <IconButton
                      onClick={() => setEditingOrderSetVariantDelivery(osv)}
                    >
                      <Warning fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Remove from Order">
                  <IconButton
                    disabled={deleteOrderSetVariant.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `You are attempting to remove Sku #${osv.variant.variantSku} from the current order, are you sure you wish to continue?`
                        )
                      ) {
                        deleteOrderSetVariant.mutate(osv.id, {
                          onError: console.error,
                        });
                      }
                    }}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>

              <div className="relative">
                <ItemPreviewImageButton
                  item={osv.variant.item}
                  variant={osv.variant}
                  imageClassName="max-h-24 border-none"
                  hideOverlay
                  size="small"
                  className="block w-full"
                />
                {osv.variant.item.includeBeacon && (
                  <BeaconIcon className="absolute -right-1 -bottom-1 pointer-events-none" />
                )}
              </div>
              <Typography className="mt-2 font-medium text-center">
                <Brands brands={osv.variant.item.brands} />
              </Typography>
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell className="sticky left-0 bg-white border-t border-neutral-200 border-b-0">
            <div className="flex w-full justify-between items-center">
              <Typography>Order Details</Typography>
              <StateCodeFilter
                stateCodeFilter={stateCodeFilter}
                setStateCodeFilter={setStateCodeFilter}
              />
              <IconButton
                aria-label="expand row"
                onClick={() => {
                  setOrderDetailsOpen(!orderDetailsOpen);
                }}
                size="large"
              >
                {orderDetailsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </div>
          </TableCell>
          {orderSetVariants.map((osv) => (
            <TableCell
              key={`sku-${osv.id}`}
              className="border-t border-neutral-200 border-b-0 text-center"
            >
              <Typography variant="body2" color="textSecondary">
                {/* TODO: Do we want run number to display here? */}
                {`${osv.variant.item.type} | ${osv.variant.variantSku}`}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell colSpan={orderSetVariants.length + 1} className="p-0">
            <Collapse in={orderDetailsOpen} timeout="auto">
              <Table
                size="small"
                className="order-set-subtable border-separate"
              >
                <TableBody>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-white border-t border-neutral-200">
                      Item Description
                    </TableCell>
                    {orderSetVariants.map((osv, i) => (
                      <TableCell
                        key={`variant-names-${i}`}
                        className="border-t border-neutral-200"
                      >
                        {osv.variant.selectedVariantOptions.length > 0
                          ? `${getVariantName(osv.variant)} - ${
                              osv.variant.item.comment
                            }`
                          : osv.variant.item.comment}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Items Per Pack</TableCell>
                    {orderSetVariants.map((osv) => (
                      <TableCell key={`pack-size-${osv.id}`}>
                        {osv.variant.item.qtyPerPack}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>MOQ</TableCell>
                    {orderSetVariants.map((osv) => (
                      <TableCell key={`moq-${osv.id}`}>
                        {osv.moq ?? "---"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Total Qty of Items</TableCell>
                    {orderSetVariants.map((osv) => (
                      <TableCell key={osv.id}>{osv.totalQty}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {isCoopOrderSet ? "Item Cost" : "Item Est Cost"}
                    </TableCell>
                    {orderSetVariants.map((osv) => (
                      <TableCell key={`price-${osv.id}`}>
                        <div className="inline-flex items-center gap-2">
                          <CompareCosts
                            cost={getCost(osv.estimatedCost, isCoopOrderSet)}
                            comparedTo={osv.estimatedCost}
                          />
                        </div>
                        {(!!osv.variablePricingMaxRangeValue ||
                          !!osv.variablePricingMinRangeValue) && (
                          <span className="ml-4 bg-neutral-200 text-neutral-700 text-xs px-1 py-0.5 rounded">
                            Tier {osv.variablePricingMinRangeValue || 0}
                            {" - "}
                            {osv.variablePricingMaxRangeValue || "♾️"}
                          </span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {isCoopOrderSet ? "Total Cost" : "Total Est Cost"}
                    </TableCell>
                    {orderSetVariants.map((osv) => (
                      <TableCell key={osv.id}>
                        <CompareCosts
                          cost={
                            isCoopOrderSet
                              ? getCoopCost(osv.totalEstimatedCost)
                              : osv.billableTotalEstimatedCost
                          }
                          comparedTo={osv.totalEstimatedCost}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </TableHead>
    </>
  );
};

export default OrderSetHead;
